import { formatDate } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { noWhitespace } from '../../../shared/validators/no-whitespace.validator';
import { RichTextEditorComponent } from '../campaign-components/rich-text-editor/rich-text-editor.component';
import { CampaignService } from '../campaign-service/campaign-service';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { HttpStatusCode } from 'axios';
import {
  TEMPLATE_CHANNELS,
  TEMPLATE_IMAGE_CIDS,
  TemplateChannel,
  TemplateImage,
  TemplateImageSlot,
  TemplatePayload
} from './create-template-model/create-template-model';
import { ConfirmPopupComponent } from '../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { MatDialog } from '@angular/material/dialog';

/**
 * Compose — "Create Template": name a reusable email template, describe what it is for, compose
 * its message, attach the header and footer banners, and watch the rendered result update as you
 * type.
 *
 * The author composes visually in `app-rich-text-editor` and never writes markup. On save,
 * `composeHtmlDocument()` turns that composition into one complete HTML document — the value the
 * backend stores as `messageTemplate`.
 *
 * The same method builds the preview, with two differences that matter:
 *
 *   | | Preview | Saved payload |
 *   |---|---|---|
 *   | Merge fields | resolved to sample values | left literal, for the backend to substitute |
 *   | Images | inline `data:` URLs | `cid:` references to the attached bytes |
 *
 * That split is deliberate: the preview has to *look* finished, while the payload has to stay
 * substitutable and must not carry the image bytes twice.
 *
 * ⚠️ Save is UI-only. No template-create endpoint exists in `environment.apis` yet, so
 * `onSaveTemplate()` assembles the full `TemplatePayload` and stops short of sending it. See the
 * TODO there.
 */
@Component({
  selector: 'app-create-template',
  // providers: [provideNativeDateAdapter()],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
  ],
  imports: [
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    PiasLayoutComponent,
    RichTextEditorComponent
  ],
  templateUrl: './create-template.component.html',
  styleUrl: './create-template.component.scss'
})
export class CreateTemplateComponent implements OnInit {

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private snackbarService = inject(SnackbarService);
  private readonly matDialog = inject(MatDialog);
  private sessionStorageService = inject(SessionStorageService);
  private campaignService = inject(CampaignService);
  private route = inject(ActivatedRoute);
  protected templateId: number | null = null;
  protected isView = false;
  protected isLoading = false;   // loading spinner state — template spinner UI if doesn't exist it adds.

  /** Base64 inflates a file by roughly a third, so the ceiling is deliberately conservative. */
  private readonly MAX_IMAGE_SIZE_MB: number = 5;

  protected readonly ACCEPTED_IMAGE_TYPES: string = 'image/png,image/jpeg,image/gif,image/webp';

  protected readonly BODY_PLACEHOLDER: string =
    'Hi {{name}}, write your message here…';

  /**
   * Stand-ins shown in the preview wherever a merge field appears. Keys are matched without the
   * braces and case-insensitively, so `{{name}}` and `{{ Name }}` both resolve.
   *
   * ⚠️ Invented for preview purposes — no backend defines the supported merge fields yet.
   */
  private readonly SAMPLE_MERGE_VALUES: Record<string, string> = {
    name: 'Wei Ling Tan',
    firstname: 'Wei Ling',
    lastname: 'Tan',
    email: 'weiling.tan@example.com',
    advisor: 'Marcus Lee',
    company: 'PIAS'
  };

  /**
   * Merge field placeholders, braces included, for the chips under the editor. Built here rather
   * than in the template because Angular decodes `&#123;` before parsing and would read the result
   * as an interpolation.
   */
  protected readonly MERGE_FIELDS: ReadonlyArray<string> = Object.keys(this.SAMPLE_MERGE_VALUES)
    .map((field) => `{{${field}}}`);

  protected templateFg!: FormGroup;
  protected channels: TemplateChannel[] = TEMPLATE_CHANNELS;

  /** The two banner slots. `null` until an image is picked for that slot. */
  protected headerImage: TemplateImage | null = null;
  protected footerImage: TemplateImage | null = null;

  protected message = 'This field is required';

  /**
   * The rendered preview document, fed to the iframe's `srcdoc`.
   *
   * Rebuilt only when the form or an image slot actually changes — `bypassSecurityTrustHtml()`
   * returns a new object on every call, and binding it directly in the template would tear down
   * and re-create the iframe on every change-detection pass.
   */
  protected previewDoc: SafeHtml = '';

  /**
   * Whether the preview pane is showing. Starts closed so the form gets the full width; the author
   * opens it with "Show Preview" when they want to check the rendered result.
   * `previewDoc` keeps updating while collapsed, so opening shows current content immediately.
   */
  protected previewOpen: boolean = false;

  // ngOnInit(): void {
  //   this.initializeForm();

  //   // Plain RxJS subscribe + assign to a field, matching the pattern used across the repo.
  //   this.templateFg.valueChanges.subscribe(() => this.refreshPreview());
  //   this.refreshPreview();
  // }
  async ngOnInit(): Promise<void> {
    this.initializeForm();
    this.templateFg.valueChanges.subscribe(() => this.refreshPreview());
    this.templateFg.get('channel')?.valueChanges.subscribe((channel) => {
      const subject = this.templateFg.get('subject');
      const messageBody = this.templateFg.get('messageBody');
      const templateSid = this.templateFg.get('templateSid');

      if (channel === 'email') {
        subject?.setValidators([Validators.required, noWhitespace]);
        messageBody?.setValidators([Validators.required]);
        templateSid?.clearValidators();
        templateSid?.setValue('', { emitEvent: false });
      } else {
        templateSid?.setValidators([Validators.required, noWhitespace]);
        subject?.clearValidators();
        messageBody?.clearValidators();
        subject?.setValue('', { emitEvent: false });       // changed
        messageBody?.setValue('', { emitEvent: false });   // changed
      }

      subject?.updateValueAndValidity();
      messageBody?.updateValueAndValidity();
      templateSid?.updateValueAndValidity();
    });

    const id = Number(this.route.snapshot.queryParamMap.get('id'));

    // if (id) {
    //   this.templateId = id;
    //   this.isView = this.route.snapshot.queryParamMap.get('mode') === 'view';

    //   const template = (await this.campaignService.getTemplate(id))[0]; // API call
    //   console.log('gettemplate response:', template);

    //   this.templateFg.patchValue({
    //     templateName: template.name,
    //     description: template.description,
    //     channel: template.channel,
    //     startDate: (template.startDate ?? template.start_Date) ? new Date(template.startDate ?? template.start_Date) : null,
    //     endDate: (template.endDate ?? template.end_Date) ? new Date(template.endDate ?? template.end_Date) : null,
    //     subject: template.subject,
    //     templateSid: template.template_sid ?? template.templateSid ?? '',
    //     // messageBody: new DOMParser()
    //     //   .parseFromString(template.messageTemplate ?? '', 'text/html').body.innerHTML
    //     messageBody: (() => {
    //       const doc = new DOMParser().parseFromString(template.messageTemplate ?? '', 'text/html');
    //       doc.querySelectorAll('img.banner-header, img.banner-footer').forEach((el) => el.remove());
    //       return doc.body.innerHTML;
    //     })()
    //   });

    //   // Backend may send the media as an array of rows with a cid (headerimage / footerimage)
    //   const media: any[] = template.media ?? template.templateMedia ?? template.template_media ?? [];

    //   const header = template.headerImage ?? media.find((m) => m.cid === 'headerimage');
    //   const footer = template.footerImage ?? media.find((m) => m.cid === 'footerimage');

    //   if (header?.s3Url) {
    //     this.headerImage = {
    //       attachment: { fileName: 'Header image', data: '', contentType: '' },
    //       dataUrl: header.s3Url,
    //       size: '',
    //       s3Url: header.s3Url,
    //       mediaId: header.id
    //     };
    //   }

    //   if (footer?.s3Url) {
    //     this.footerImage = {
    //       attachment: { fileName: 'Footer image', data: '', contentType: '' },
    //       dataUrl: footer.s3Url,
    //       size: '',
    //       s3Url: footer.s3Url,
    //       mediaId: footer.id
    //     };
    //   }
    //   // if (this.isView) this.templateFg.disable();
    // }
    if (id) {
      this.templateId = id;
      this.isView = this.route.snapshot.queryParamMap.get('mode') === 'view';

      // API returns { dashboard_template: [...], template_files: {...} }, so read the first template from the array
      const response = await this.campaignService.getTemplate(id); // API call
      const template =
        response?.dashboard_template?.[0] ??
        response?.responsedata?.dashboard_template?.[0];

      // Stop here if the backend returned no template, otherwise template.name would crash
      if (!template) {
        this.snackbarService.openFailureSnackBar('Template not found.');
        return;
      }
      console.log('gettemplate response:', template);

      this.templateFg.patchValue({
        templateName: template.name,
        description: template.description,
        channel: template.channel,
        startDate: (template.startDate ?? template.start_Date) ? new Date(template.startDate ?? template.start_Date) : null,
        endDate: (template.endDate ?? template.end_Date) ? new Date(template.endDate ?? template.end_Date) : null,
        subject: template.subject,
        templateSid: template.template_sid ?? template.templateSid ?? '',
        messageBody: (() => {
          const doc = new DOMParser().parseFromString(template.messageTemplate ?? '', 'text/html');
          doc.querySelectorAll('img.banner-header, img.banner-footer').forEach((el) => el.remove());
          return doc.body.innerHTML;
        })()
      });

      // Backend may send the media as an array of rows with a cid (headerimage / footerimage)
      const media: any[] = template.media ?? template.templateMedia ?? template.template_media ?? [];

      const header = template.headerImage ?? media.find((m) => m.cid === 'headerimage');
      const footer = template.footerImage ?? media.find((m) => m.cid === 'footerimage');

      if (header?.s3Url) {
        this.headerImage = {
          attachment: { fileName: 'Header image', data: '', contentType: '' },
          dataUrl: header.s3Url,
          size: '',
          s3Url: header.s3Url,
          mediaId: header.id
        };
      }

      if (footer?.s3Url) {
        this.footerImage = {
          attachment: { fileName: 'Footer image', data: '', contentType: '' },
          dataUrl: footer.s3Url,
          size: '',
          s3Url: footer.s3Url,
          mediaId: footer.id
        };
      }
      // if (this.isView) this.templateFg.disable();
    }

    this.refreshPreview();
  }

  private initializeForm(): void {
    this.templateFg = this.fb.group({
      templateName: ['', [Validators.required, noWhitespace]],
      description: [''],
      channel: [this.channels[0].value, Validators.required],
      // End-before-start is caught by the end picker's `[min]` binding (`matDatepickerMin`).
      startDate: [null as Date | null, Validators.required],
      endDate: [null as Date | null, Validators.required],
      subject: ['', [Validators.required, noWhitespace]],
      messageBody: ['', Validators.required],
      templateSid: ['']
    });
  }


  // ─────────────────────────── Images ───────────────────────────

  protected async onImageSelected(event: Event, slot: TemplateImageSlot): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];

    // Clearing the input up front lets the same file be re-picked after a rejection.
    input.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.snackbarService.openFailureSnackBar(`${file.name} is not an image file.`);
      return;
    }

    if (file.size > this.MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      this.snackbarService.openFailureSnackBar(
        `${file.name} is larger than ${this.MAX_IMAGE_SIZE_MB}MB.`
      );
      return;
    }

    const base64: string = await this.convertToBase64(file);
    const contentType: string = file.type || 'image/png';

    const image: TemplateImage = {
      attachment: { fileName: file.name, data: base64, contentType },
      dataUrl: `data:${contentType};base64,${base64}`,
      size: this.formatSize(file.size),
      file
    };


    // Edit mode: the template already exists (templateId), so upload the image immediately
    if (this.templateId) {
      const previous = slot === 'header' ? this.headerImage : this.footerImage;

      const uploaded = await this.campaignService
        .uploadTemplateMedia(this.templateId, file)
        .then((res) => {
          image.s3Url = res.s3Url;
          image.mediaId = res.id;
          return true;
        })
        .catch((error) => {
          this.snackbarService.openFailureSnackBar(error?.message || 'Image upload failed.');
          return false;
        });

      // If the upload failed, keep the existing image as it is
      if (!uploaded) {
        return;
      }

      // Delete the old image only after the new one has uploaded successfully
      if (previous?.mediaId) {
        await this.campaignService.deleteTemplate(previous.mediaId).catch(() => null);
      }
    }

    // Each slot holds one image, so picking again replaces rather than appends.
    if (slot === 'header') {
      this.headerImage = image;
    } else {
      this.footerImage = image;
    }

    this.refreshPreview();

  }

  // protected removeImage(slot: TemplateImageSlot): void {
  //   if (slot === 'header') {
  //     this.headerImage = null;
  //   } else {
  //     this.footerImage = null;
  //   }

  //   this.refreshPreview();
  // }
  protected removeImage(slot: TemplateImageSlot): void {
    const image = slot === 'header' ? this.headerImage : this.footerImage;

    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: `Are you sure you want to remove the ${slot} image?`,
        hidecancel: false
      }
    });

    dialogRef.afterClosed().subscribe(async (result: any) => {
      // User cancelled, so do nothing
      if (result?.data !== true) {
        return;
      }

      // Call the delete API only if the image already exists in the DB (it has a mediaId)
      if (image?.mediaId) {
        const deleted = await this.campaignService
          .deleteTemplate(image.mediaId)
          .then(() => true)
          .catch((error) => {
            this.snackbarService.openFailureSnackBar(error?.message || 'Could not remove the image.');
            return false;
          });

        // If the delete failed, keep the image in the UI
        if (!deleted) {
          return;
        }
      }

      if (slot === 'header') {
        this.headerImage = null;
      } else {
        this.footerImage = null;
      }

      this.refreshPreview();
      this.snackbarService.openSuccessSnackBar('Image removed successfully.');
    });
  }

  private convertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        const result = reader.result as string;

        // Strip the `data:<type>;base64,` prefix — the payload carries the bytes only.
        resolve(result.split(',')[1]);
      };

      reader.onerror = error => reject(error);
    });
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ─────────────────────────── Preview ───────────────────────────

  protected togglePreview(): void {
    this.previewOpen = !this.previewOpen;
  }

  private refreshPreview(): void {
    this.previewDoc = this.sanitizer.bypassSecurityTrustHtml(this.composeHtmlDocument(true));
  }


  /**
   * Turns the composed message into one complete HTML document.
   *
   * `forPreview` is what separates the two callers:
   *
   *   - `true`  — for the iframe. Merge fields resolve to sample values, images are inline `data:`
   *               URLs, a subject bar is drawn on top and empty fields show placeholder copy.
   *   - `false` — for the backend. Merge fields stay literal so they remain substitutable per
   *               recipient, images become `cid:` references to the attached bytes, and there is no
   *               subject bar because the subject travels as its own payload field.
   *
   * The preview iframe is sandboxed with neither `allow-scripts` nor `allow-same-origin`, which is
   * what makes bypassing Angular's sanitizer safe. Sanitizing instead would strip the inline styles
   * the editor emits and an email depends on.
   */
  private composeHtmlDocument(forPreview: boolean): string {
    const rawSubject: string = this.templateFg.get('subject')?.value ?? '';
    const rawBody: string = this.templateFg.get('messageBody')?.value ?? '';

    // The editor already hands us HTML, so the body is never escaped — only resolved.
    let body: string = forPreview ? this.resolveMergeFields(rawBody) : rawBody;

    if (forPreview && !rawBody.trim()) {
      body = '<p class="empty">Your message will appear here as you compose it.</p>';
    }

    const subjectBar: string = forPreview
      ? `<p class="subject">${rawSubject.trim()
        ? this.resolveMergeFields(this.escapeHtml(rawSubject))
        : '<span class="empty">No subject yet</span>'}</p>`
      : '';

    const header: string = this.renderBanner(this.headerImage, 'header', forPreview);
    const footer: string = this.renderBanner(this.footerImage, 'footer', forPreview);

    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body {
        margin: 0;
        padding: 20px;
        background: #ffffff;
        color: #1e293b;
        font-family: 'Open Sans', Arial, sans-serif;
        font-size: 14px;
        line-height: 1.6;
      }
      .subject {
        margin: 0 0 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e5e7eb;
        font-size: 16px;
        font-weight: 600;
        color: #00447f;
      }
      .banner {
        display: block;
        width: 100%;
        max-width: 100%;
        height: auto;
      }
      .banner-header { margin: 0 0 16px; }
      .banner-footer { margin: 16px 0 0; }
      .empty { color: #94a3b8; font-style: italic; font-weight: 400; }
      .merge {
        background: #e0edff;
        border-radius: 3px;
        padding: 0 3px;
        color: #1d4ed8;
      }
      .merge.unknown {
        background: #fef3c7;
        color: #b45309;
      }
      p { margin: 0 0 12px; }
      ul, ol { margin: 0 0 12px; padding-left: 24px; }
      a { color: #1d4ed8; }
      img { max-width: 100%; height: auto; }
    </style>
  </head>
  <body>
    ${subjectBar}
    ${header}
    ${body}
    ${footer}
  </body>
</html>`;
  }

  /**
   * One banner `<img>`. The preview points at the inline `data:` URL so it renders on screen; the
   * saved document points at a `cid:` reference so the bytes travel once, in the payload's
   * attachment field, rather than being duplicated inside `messageTemplate`.
   */
  private renderBanner(
    image: TemplateImage | null,
    slot: TemplateImageSlot,
    forPreview: boolean
  ): string {
    if (!image) {
      return '';
    }

    const src: string = forPreview ? image.dataUrl : `cid:${TEMPLATE_IMAGE_CIDS[slot]}`;

    return `<img class="banner banner-${slot}" src="${src}" `
      + `alt="${this.escapeHtml(image.attachment.fileName)}" />`;
  }

  /**
   * Swaps `{{field}}` placeholders for their sample values so the preview reads like a real send.
   * Placeholders with no sample behind them are highlighted rather than replaced — that is how an
   * author spots a misspelled merge field before the template goes anywhere near a campaign.
   *
   * Preview only: the saved document keeps the placeholders literal.
   */
  private resolveMergeFields(value: string): string {
    return value.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, field: string) => {
      const sample = this.SAMPLE_MERGE_VALUES[field.toLowerCase()];

      return sample
        ? `<span class="merge">${this.escapeHtml(sample)}</span>`
        : `<span class="merge unknown">${this.escapeHtml(match)}</span>`;
    });
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─────────────────────────── Actions ───────────────────────────

  // protected onSaveTemplate(): void {
  //   if (this.templateFg.invalid) {
  //     this.templateFg.markAllAsTouched();
  //     this.snackbarService.openFailureSnackBar('Please complete the required fields.');
  //     return;
  //   }

  //   const payload = new TemplatePayload();
  //   payload.setName(this.templateFg.get('templateName')?.value.trim());
  //   payload.setDescription(this.templateFg.get('description')?.value.trim());
  //   payload.setChannel(this.templateFg.get('channel')?.value);
  //   payload.setStartDate(this.toApiDate(this.templateFg.get('startDate')?.value));
  //   payload.setEndDate(this.toApiDate(this.templateFg.get('endDate')?.value));
  //   payload.setSubject(this.templateFg.get('subject')?.value.trim());

  //   // The composed message, converted to a complete HTML document for the backend.
  //   payload.setMessageTemplate(this.composeHtmlDocument(false));

  //   payload.setStatus('draft');
  //   payload.setCreatedBy(parseInt(this.sessionStorageService.getCurrentUserId(), 10));
  //   payload.setHeaderImage(this.headerImage?.attachment ?? null);
  //   payload.setFooterImage(this.footerImage?.attachment ?? null);

  //   // TODO(backend): no template-create endpoint exists in `environment.apis` yet. Once it does,
  //   // add `createTemplate()` to CampaignService following the axios + manual-Authorization pattern
  //   // and replace this block with `await this.campaignService.createTemplate(payload)`.
  //   this.snackbarService.openSuccessSnackBar('Template saved.');
  //   this.router.navigate(['/campaign/manage-template']);
  // }

  protected async onSaveTemplate(status: string = 'draft'): Promise<void> {
    if (this.templateFg.invalid) {
      this.templateFg.markAllAsTouched();
      this.snackbarService.openFailureSnackBar('Please complete the required fields.');
      return;
    }

    // Guard against a double-click firing two save calls while the first is in flight.
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    const payload = new TemplatePayload();
    payload.setName(this.templateFg.get('templateName')?.value.trim());
    payload.setDescription(this.templateFg.get('description')?.value.trim());
    payload.setChannel(this.templateFg.get('channel')?.value);
    payload.setStartDate(this.toApiDate(this.templateFg.get('startDate')?.value));
    payload.setEndDate(this.toApiDate(this.templateFg.get('endDate')?.value));
    payload.setSubject(this.templateFg.get('subject')?.value.trim());
    payload.setTemplateSid(this.templateFg.get('templateSid')?.value ?? '');

    // The composed message, converted to a complete HTML document for the backend.
    payload.setMessageTemplate(this.composeHtmlDocument(false));

    payload.setStatus(status);
    payload.setId(this.templateId ?? 0);
    payload.setCreatedBy(parseInt(this.sessionStorageService.getCurrentUserId(), 10));
    payload.setHeaderImage(this.headerImage?.attachment ?? null);
    payload.setFooterImage(this.footerImage?.attachment ?? null);

    const responseData = await this.campaignService
      .createTemplate(payload)
      .then((response) => JSON.parse(response))
      .catch((error) => error);

    if (responseData.status === HttpStatusCode.Ok || responseData.status === HttpStatusCode.Created) {
      const hasImages = !!(this.headerImage?.file || this.footerImage?.file);

      if (hasImages) {
        const savedId: number | null =
          this.templateId
          ?? responseData.data?.responsedata?.id
          ?? await this.findCreatedTemplateId(this.templateFg.get('templateName')?.value.trim());

        if (savedId) {
          await this.uploadImages(savedId);
        } else {
          this.snackbarService.openFailureSnackBar('Template saved, but image upload could not find the template id.');
        }
      }
    }

    this.isLoading = false;


    if (
      responseData.status === HttpStatusCode.Ok || responseData.status === HttpStatusCode.Created
    ) {
      this.snackbarService.openSuccessSnackBar(responseData.data?.message ?? 'Template saved.');
      this.router.navigate(['/campaign/manage-template']);
    } else {
      // Service throws AxiosError(..., "401"), so `code` is a string. Convert before comparing.
      if (Number(responseData.code) === HttpStatusCode.Unauthorized || responseData.status === HttpStatusCode.Unauthorized) {
        this.snackbarService.openFailureSnackBar('Session timed out. Try logging in again!');
        this.router.navigate(['/login']);
      } else {
        this.snackbarService.openFailureSnackBar('We couldn\'t save the template. Please try again');
      }
    }
  }

  /** ⚠️ Assumes the backend takes calendar dates as `yyyy-MM-dd` — confirm against the Go struct. */
  // private toApiDate(date: Date): string {
  //   return formatDate(date, 'yyyy-MM-dd', 'en-US');
  // }
  private toApiDate(date: Date): string {
    return `${formatDate(date, 'yyyy-MM-dd', 'en-US')}T00:00:00Z`;
  }

  protected get isEmail(): boolean {
    return this.templateFg.get('channel')?.value === 'email';
  }

  private async uploadImages(templateId: number): Promise<void> {
    for (const image of [this.headerImage, this.footerImage]) {
      if (image?.file && !image.s3Url) {
        await this.campaignService
          .uploadTemplateMedia(templateId, image.file)
          .then((res) => {
            image.s3Url = res.s3Url;
            image.mediaId = res.id
          })
          .catch((error) => {
            console.error('Image upload error:', error);
            this.snackbarService.openFailureSnackBar(
              error?.message || 'Template saved, but image upload failed.'
            );
          });
      }
    }
  }

  // Temporary: backend create response when it get's id
  private async findCreatedTemplateId(name: string): Promise<number | null> {
    return await this.campaignService
      .listTemplates()
      .then((list) => {
        const matches = list.filter((t) => t.name === name);
        return matches.length ? Math.max(...matches.map((t) => t.id)) : null;
      })
      .catch(() => null);
  }




  protected onCancel(): void {
    this.router.navigate(['/campaign/manage-template']);
  }
}
